/**
 * MG Cart Incentives — Maria Galland Paris
 * Built by KONVOI (konvoi.co) · Shopify Plus relaunch 2026
 *
 * Two reward mechanics, shared between the cart drawer and the cart page:
 *   1. Gift Ladder   — continuous progress bar (free shipping + 3 gift tiers).
 *                      When a tier is reached, its configured gift product is
 *                      auto-added to the cart; removed when the cart drops below.
 *   2. Sample Picker — the shopper chooses up to N free testers from a curated
 *                      collection. Each pick becomes a real cart line.
 *   4. Sale-aware ladder — CR-02, added 2026-10-02. With the theme setting "Threshold gifts:
 *                      do not count sale products" on, the threshold gifts and the progress
 *                      bar count only the products that are not on sale (ladderAmounts).
 *   3. Gift Rules    — CR-01, added 2026-09-22. Up to five conditional promotions that
 *                      MG configures in the theme editor: spend in a set of collections,
 *                      or a number of products from them, optionally gated behind a promo
 *                      code, optionally exclusive with the gift ladder. Rule data that
 *                      only the server can answer (collection membership of the cart
 *                      lines, date window, market, applied codes) comes from the section
 *                      sections/mg-cart-rules-data.liquid.
 *                      Since 2026-10-13 a promo code that Shopify does not apply yet gets one
 *                      try per cart state: the gift is added, and the next cart read decides
 *                      whether it stays (see "rule codes: add the gift first").
 *
 * "Free items" mechanism (gifts AND samples):
 *   Both are NORMAL products added with a HIDDEN line-item property
 *   (`_mg_gift` / `_mg_sample`, underscore = not shown to the shopper). A 100%
 *   automatic discount (or a Shopify Function at go-live) targets those tagged
 *   lines and prices them to 0. We never create real €0 variants — that would
 *   let anyone add them standalone for free and can't be quantity-capped.
 *
 * Master switch — config.free_items_live (theme setting "Activate free gifts &
 * samples"):
 *   OFF  → DISPLAY MODE. Ladder is visual only; chosen samples are saved as a
 *          cart note (`samples_selection`) for the SAP pick list. NOTHING is
 *          added as a paid line. Safe until the 100% discount exists.
 *   ON   → LIVE MODE. Gifts auto-add at each threshold; chosen samples are added
 *          as real cart lines. All tagged for the 100%-off discount. Flip this on
 *          only after the discount/Function is configured (or on the dev theme to
 *          test the full flow).
 *
 * Identifiers:
 *   - Sample checkbox `value`          = product.id  (matches cart line.product_id)
 *   - Sample checkbox `data-variant-id`= variant.id  (used for /cart/add.js)
 *   - `samples_selection` attribute    = comma-separated product IDs (SAP summary)
 *
 * Cart-change channels we react to:
 *   - FoxTheme.pubsub `cart-update`  (Sleek — the ONLY reliable in-drawer signal)
 *   - DOM `cart:updated`             (fired by most other paths + by us)
 */
(function () {
  'use strict';

  // Debug handle: window.mgCI.state()
  window.mgCI = {
    v: '2026-10-13-cr01-code-trial',
    booted: false,
    lastTotal: null,
    pubsubBound: false,
    events: 0,
    lastRules: null,
    // True while a cart read or a free-item mutation is still running. Storefront tests wait
    // on this instead of on a timeout, which is what made the gift-rules suite flaky.
    busy: function () { return readsInFlight > 0 || giftSyncInFlight || !!rulesRetryTimer || codeChangeInFlight || codeCheckInFlight; },
    // MG-PATCH 2026-09-27 (CR-01): read by the coupon form in assets/cart.js.
    isRuleCode: function (code) { return rulesEnabled() && !!ruleByCode(code); },
    hiddenRuleCodes: function () { return rulesEnabled() ? hiddenRuleCodes() : []; },
    // MG-PATCH 2026-10-13 (CR-01): the coupon form hands a rule code that Shopify does not apply yet
    // to the engine, which tries it (gift in, Shopify decides) and answers with the outcome:
    // { state: 'applied' | 'rejected' | 'pending' | 'single' | 'unknown', changed }.
    verifyRuleCode: function (code) { return verifyRuleCode(code); },
    // Re-renders Sleek's cart with fresh sections (the coupon form's own sections predate the try).
    publishCart: function (source) { return publishSleek(null, source || 'mg-rule-code'); },
    // Last code tries, newest first: { code, ruleId, result, giftFreeByOtherDiscount }.
    codeTrials: [],
    lastVerdict: null,
    lastAmounts: null,
    // MG-PATCH 2026-10-02 (CR-02): what the ladder counts for a given cart, for storefront tests.
    amounts: function (cart) { return ladderAmounts(cart); },
    state: function () {
      const ladders = document.querySelectorAll('[data-mg-gift-ladder]');
      const fills = document.querySelectorAll('[data-mg-gift-progress]');
      const cfg = readConfig();
      return {
        version: window.mgCI.v,
        booted: window.mgCI.booted,
        pubsubBound: window.mgCI.pubsubBound,
        foxPubsub: !!(window.FoxTheme && window.FoxTheme.pubsub),
        freeItemsLive: isFreeItemsLive(),
        rulesEnabled: rulesEnabled(),
        saleAware: saleAware(),
        lastAmounts: window.mgCI.lastAmounts || null,
        rulesData: window.mgCI.lastRules || readRulesData(),
        cartUpdateEventsSeen: window.mgCI.events,
        lastTotalCents: window.mgCI.lastTotal,
        ladderCount: ladders.length,
        fillWidths: Array.prototype.map.call(fills, function (f) { return f.style.width; }),
        config: cfg
      };
    }
  };

  // ─────────────────────────────────────────────
  //  Shared config
  // ─────────────────────────────────────────────

  function readConfig() {
    const el = document.getElementById('mg-cart-incentives-config');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  // Master switch (theme setting "Activate free gifts & samples").
  function isFreeItemsLive() {
    const cfg = readConfig();
    return !!(cfg && cfg.free_items_live === true);
  }

  // MG-PATCH 2026-09-23 (CR-01): Shopify throttles the cart endpoints under load (429) and
  // turns a mutation away while another one holds the cart (409). fetch() resolves on both,
  // so a throttled gift add used to vanish silently until the next cart event. Up to two
  // retries with a short backoff; Retry-After wins when Shopify sends one (capped at 3 s).
  const RETRY_DELAYS_MS = [400, 1200];
  function fetchWithRetry(url, init, attempt) {
    attempt = attempt || 0;
    return fetch(url, init).then(function (r) {
      if ((r.status !== 409 && r.status !== 429) || attempt >= RETRY_DELAYS_MS.length) return r;
      const after = Number(r.headers.get('Retry-After'));
      const wait = after > 0 ? Math.min(after * 1000, 3000) : RETRY_DELAYS_MS[attempt];
      return new Promise(function (resolve) { setTimeout(resolve, wait); })
        .then(function () { return fetchWithRetry(url, init, attempt + 1); });
    });
  }

  function postJSON(url, body) {
    return fetchWithRetry(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    });
  }

  function getCart() {
    return fetchWithRetry('/cart.js', { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('/cart.js answered ' + r.status);
      return r.json();
    });
  }

  // ─────────────────────────────────────────────
  //  MG-PATCH 2026-09-22 (CR-01) — gift rules data
  // ─────────────────────────────────────────────
  //
  // Everything the rules need but /cart.js cannot answer is rendered server side by
  // sections/mg-cart-rules-data.liquid: which of the rule collections each cart line
  // belongs to (Liquid's product.collections; there is no JavaScript equivalent, and
  // going the other way round is capped at 50 products per collection), which rules are
  // inside their date window and their market, and which discount codes the cart carries.
  //
  // The island is rendered into every page, so the DOM copy is correct on load. After a
  // cart change it is re-requested through the Section Rendering API, which returns the
  // authoritative cart AND the freshly rendered section in ONE round trip — so the rules
  // path costs the same number of requests as the plain /cart.js read it replaces.

  const RULES_SECTION = 'mg-cart-rules-data';

  function parseRulesJSON(text) {
    try {
      const data = JSON.parse(text);
      if (!data || typeof data !== 'object') return null;
      if (!Array.isArray(data.rules)) data.rules = [];
      if (!Array.isArray(data.lines)) data.lines = [];
      if (!Array.isArray(data.discountCodes)) data.discountCodes = [];
      if (!Array.isArray(data.discountTitles)) data.discountTitles = [];
      return data;
    } catch (e) { return null; }
  }

  function readRulesData() {
    const el = document.getElementById('mg-gift-rules-data');
    return el ? parseRulesJSON(el.textContent) : null;
  }

  // Master switch, read from the cart-incentives config so a shop without gift rules
  // never pays for the extra section render or the section request.
  function rulesEnabled() {
    const cfg = readConfig();
    return !!(cfg && cfg.rules_enabled === true && isFreeItemsLive());
  }

  // ─────────────────────────────────────────────
  //  MG-PATCH 2026-10-02 (CR-02) — threshold gifts during sales
  // ─────────────────────────────────────────────
  //
  // Theme setting "Threshold gifts: do not count sale products" (sale_aware). A paid line is a
  // sale product when
  //   - a discount that is not a code applies to the line itself (automatic discount on
  //     products; Shopify lists it in line_level_discount_allocations with type "automatic")
  //   - a discount CODE applies to the line and the code is listed in the setting "Promo codes
  //     that count as a sale" (sale_codes). Codes are not a sale by default: in this shop the
  //     welcome code and the influencer codes are product discounts too (verified 02.10.2026,
  //     WELCOME arrives as a line allocation of type "discount_code", target "entitled"), and MG
  //     wants them to keep the gifts (approval of 01.10.2026)
  //   - a listed code applies to the whole order (cart_level_discount_applications): every
  //     paid line counts as sale then
  //   - the variant has a compare-at price above the line's price before discounts
  //     (strikethrough price). /cart.js has no compare-at price, so it comes from the rules data
  //     section, which renders it per line (sections/mg-cart-rules-data.liquid)
  // Gift and sample lines are never sale products and never count, as before. The same
  // classification runs server side in snippets/mg-cart-gift-ladder.liquid for the first paint.
  function saleAware() {
    const cfg = readConfig();
    return !!(cfg && cfg.sale_aware === true);
  }

  function saleCodes() {
    const cfg = readConfig();
    const found = {};
    String((cfg && cfg.sale_codes) || '').split(',').forEach(function (c) {
      const k = c.trim().toLowerCase();
      if (k) found[k] = true;
    });
    return found;
  }

  // The rules data section is requested after every cart change when the gift rules need it OR
  // the sale-aware ladder needs the compare-at prices. Same single request either way.
  function islandNeeded() {
    return rulesEnabled() || (saleAware() && isFreeItemsLive());
  }

  // Compare-at price per variant, from every rules island this page has seen. A compare-at price
  // belongs to the variant, not to the cart, so an older read still answers it when a fresh
  // section render is missing (Shopify returns the section as null under load).
  const compareAtByVariant = {};
  function rememberCompareAt(island) {
    ((island && island.lines) || []).forEach(function (line) {
      if (line && line.variantId) compareAtByVariant[String(line.variantId)] = Number(line.compareAt) || 0;
    });
  }

  // What the ladder counts, in cents:
  //   gift      threshold gifts, progress bar, "still x to go" towards a gift tier, "x/3 gifts"
  //   shipping  the free-shipping milestone: the whole paid cart, as Shopify's shipping rate
  //   saleLines number of paid lines on sale (the explanatory line shows while > 0)
  //   unknown   true when a line's compare-at price is not known yet; the reconcile then leaves
  //             the threshold gifts as they are until a fresh read arrives
  // An order discount is shared out over the paid lines in proportion, so a cart without sale
  // products counts exactly what qualifyingTotalCents counted before CR-02.
  function ladderAmounts(cart) {
    const base = qualifyingTotalCents(cart);
    const result = { gift: base, shipping: base, saleLines: 0, unknown: false };
    if (!saleAware() || !cart) return result;

    const codes = saleCodes();
    const isSaleCode = function (title) {
      return typeof title === 'string' && !!codes[title.trim().toLowerCase()];
    };
    const orderSale = ((cart.cart_level_discount_applications) || []).some(function (app) {
      return app && app.type === 'discount_code' && isSaleCode(app.title);
    });

    let paid = 0;
    let nonSale = 0;
    (cart.items || []).forEach(function (item) {
      if (isFreeLine(item)) return;
      const price = item.final_line_price || 0;
      paid += price;
      let sale = orderSale;
      if (!sale) {
        sale = (item.line_level_discount_allocations || []).some(function (a) {
          const app = (a && a.discount_application) || {};
          if (!(a && a.amount > 0)) return false;
          return app.type === 'discount_code' ? isSaleCode(app.title) : true;
        });
      }
      if (!sale) {
        const vId = String(item.variant_id || item.id);
        if (compareAtByVariant.hasOwnProperty(vId)) {
          sale = compareAtByVariant[vId] > (item.original_price || 0);
        } else {
          result.unknown = true;
        }
      }
      if (sale) result.saleLines++;
      else nonSale += price;
    });
    result.gift = paid > 0 ? Math.round(nonSale * base / paid) : 0;
    return result;
  }

  // The section arrives as a rendered HTML string. Parsed in an inert document (DOMParser
  // neither runs scripts nor loads resources), and only the JSON island is read out.
  function rulesDataFromSections(sections) {
    const html = sections && sections[RULES_SECTION];
    if (typeof html !== 'string') return null;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.getElementById('mg-gift-rules-data');
    return el ? parseRulesJSON(el.textContent) : null;
  }

  // Cart + rules context in one request. Falls back to a plain /cart.js read whenever the
  // section cannot be rendered, so a broken section never breaks the gift ladder.
  // MG-PATCH 2026-09-23 (CR-01): the fallback no longer hands over the island the page was
  // rendered with. Its lines describe the cart at page load, and judging the rules by them
  // held rule gifts back or took valid ones out (Shopify returns a section as null when it
  // cannot render it in time, which happens under load). `rulesStale` tells the reconcile to
  // leave the rule lines alone until a fresh read arrives.
  let lastFreshRules = null;
  function getCartWithRules() {
    return postJSON('/cart/update.js', { sections: RULES_SECTION, sections_url: location.pathname })
      .then(function (r) {
        if (!r.ok) throw new Error('/cart/update.js answered ' + r.status);
        return r.json();
      })
      .then(function (cart) {
        const fresh = rulesDataFromSections(cart.sections);
        if (fresh) { lastFreshRules = fresh; rememberCompareAt(fresh); }
        return { cart: cart, rules: fresh, rulesStale: !fresh };
      })
      .catch(function () {
        return getCart().then(function (cart) { return { cart: cart, rules: null, rulesStale: true }; });
      });
  }

  // Rule settings (not the lines) are the same on every read of a page, so the last fresh
  // read — or the island the page was rendered with — can still say which rule is exclusive.
  function ruleIsExclusive(ruleId) {
    const known = lastFreshRules || readRulesData();
    const list = (known && known.rules) || [];
    for (let i = 0; i < list.length; i++) {
      if (list[i].id === ruleId) return list[i].combinable === false;
    }
    return false;
  }

  function localeRoot() {
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return typeof root === 'string' && root ? root.replace(/\/$/, '') : '';
  }

  // Live stock check before a rule gift is added ("while stocks last"). The server-rendered
  // `available` flag can be minutes old on a cached page, so the product JSON decides.
  // Cached per handle for the page view — a gift that just sold out stays out until reload.
  //
  // Resolves to null when the product JSON cannot answer. That is not a theoretical case:
  // a gift product can be unlisted or renamed, and /products/<handle>.js then answers 404
  // (verified 2026-09-22 with the former tier-3 gift 460-nutrivital-cream-travel). Treating
  // that as "sold out" would silently switch a promotion off, so the caller falls back to
  // the flag the section rendered instead.
  const stockCache = {};
  function variantAvailable(handle, variantId) {
    if (!handle) return Promise.resolve(null);
    if (!stockCache[handle]) {
      stockCache[handle] = fetch(localeRoot() + '/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) { return (p && p.variants) || null; })
        .catch(function () { return null; });
    }
    return stockCache[handle].then(function (variants) {
      if (!variants) return null;
      for (let i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(variantId)) return !!variants[i].available;
      }
      return null; // variant not in the product JSON — do not read that as "sold out"
    });
  }

  // Which discount codes does this cart carry? Shopify exposes them in more than one
  // place depending on how the code was applied, so all known channels are merged and
  // compared case-insensitively:
  //   - cart.discount_codes (Liquid, rendered into the section)
  //   - cart.discount_codes (Ajax cart, when the storefront returns it)
  //   - the titles of the applied discount applications — a code discount is titled with
  //     the code itself, which is the fallback if neither list is available.
  // MG-PATCH 2026-09-30 (CR-01, MGP test round 3): strict. A code counts only when Shopify
  // really applies it. From 27.09. to 30.09. a code that Shopify reported as `applicable: false`
  // counted as well, and an expired code (HYDRA, expired 28.09.) kept bringing the gift. Shopify
  // does not say WHY a code is not applicable (expired, unknown, not combinable, its own
  // conditions not met), so the only safe reading is: not applied, no gift.
  //   - the Ajax cart's discount_codes carry the flag; `applicable: true` counts, and a code the
  //     Ajax cart marks `applicable: false` never counts, whatever another channel says
  //   - the titles of the discount applications (cart level, line level, the section's list)
  //     count, because a code discount only has an application when Shopify applies it
  //   - the section's own code list is only read when the Ajax cart has none
  function appliedCodes(cart, rules) {
    const found = {};
    function key(value) { return typeof value === 'string' && value.trim() ? value.trim().toLowerCase() : ''; }
    function push(value) { const k = key(value); if (k) found[k] = true; }
    const ajaxCodes = cart && Array.isArray(cart.discount_codes) ? cart.discount_codes : null;
    if (ajaxCodes) {
      ajaxCodes.forEach(function (c) {
        if (typeof c === 'string') push(c);
        else if (c && c.applicable === true) push(c.code);
      });
    } else {
      ((rules && rules.discountCodes) || []).forEach(function (c) { if (c && c.applicable === true) push(c.code); });
    }
    ((rules && rules.discountTitles) || []).forEach(push);
    ((cart && cart.cart_level_discount_applications) || []).forEach(function (d) { push(d && d.title); });
    ((cart && cart.items) || []).forEach(function (item) {
      ((item && item.line_level_discount_allocations) || []).forEach(function (a) {
        push(a && a.discount_application && a.discount_application.title);
      });
    });
    if (ajaxCodes) {
      ajaxCodes.forEach(function (c) {
        if (c && typeof c === 'object' && c.applicable === false) delete found[key(c.code)];
      });
    }
    return found;
  }

  // ─────────────────────────────────────────────
  //  MG-PATCH 2026-10-13 (CR-01) — rule codes: add the gift first, then let Shopify confirm
  // ─────────────────────────────────────────────
  //
  // MG sets up a gift code as a 100 % discount on the gift product itself (HYDRA: 100 % on the
  // 15 ml serum, also as buy X get Y on it). Shopify can only apply such a code once the gift is in
  // the cart, and the strict mode of 30.09. waited for the code before it added the gift: the theme
  // waited for Shopify, Shopify for the gift, and nothing happened. Before 30.09. every entered code
  // counted, and an expired code kept bringing the gift. Now:
  //   - every code the shopper entered counts as entered: cart.discount_codes holds it with
  //     `applicable` true or false, whichever way it came in (coupon form in the drawer or on the
  //     cart page, /discount/CODE link, a code kept from an earlier visit)
  //   - when an entered code belongs to a rule whose other conditions hold (count or spend from the
  //     collections, market, country list, dates, gift in stock), the gift is added on trial
  //   - the next cart read decides: Shopify applies the code now → the gift stays. The code is still
  //     not applicable (expired, unknown, not combinable) → the gift goes again, and the code is
  //     remembered as rejected for this cart and this cart state (sessionStorage: cart token + code
  //     → the paid lines). The next try comes when the paid lines change or the code is entered again.
  // One try per code and cart state, inside the one reconcile pass, so the cart is never mutated in
  // parallel (409 cart lock, 15.07.). A try that Shopify turns down leaves the cart as it was and is
  // not re-rendered, so neither the drawer nor the cart page shows the gift coming and going.
  //
  // Verified 13.10.2026: while the automatic discount "Free gift" already makes the gift product free,
  // Shopify applies only one product discount per cart line, keeps the automatic one and reports the
  // code as not applicable, valid or expired alike. Such a code ends up rejected after its try. A
  // code rule works when the code itself is what makes its gift free (gift product not in "Free gift").
  const CODE_REJECTED_KEY = 'mg_rule_code_rejected';

  function codeKey(code) { return typeof code === 'string' ? code.trim().toLowerCase() : ''; }

  // Every code the cart holds, applied or not. The Ajax cart is authoritative; the section's list
  // stands in when the Ajax cart has none.
  function enteredCodes(cart, rules) {
    const found = {};
    const list = cart && Array.isArray(cart.discount_codes) ? cart.discount_codes : ((rules && rules.discountCodes) || []);
    list.forEach(function (c) {
      const k = codeKey(typeof c === 'string' ? c : c && c.code);
      if (k) found[k] = true;
    });
    return found;
  }

  // The cart state a rejection belongs to: the paid lines. Free lines (the trial gift itself,
  // threshold gifts, samples) never change it, so adding and removing the trial gift does not
  // open a second try.
  function cartStateSignature(cart) {
    return ((cart && cart.items) || [])
      .filter(function (it) { return !isFreeLine(it); })
      .map(function (it) { return String(it.variant_id || it.id) + 'x' + (it.quantity || 0); })
      .sort()
      .join(',');
  }

  function cartTokenOf(cart) { return String((cart && cart.token) || '').split('?')[0]; }

  function readRejections() {
    try {
      const map = JSON.parse(sessionStorage.getItem(CODE_REJECTED_KEY) || '{}');
      return map && typeof map === 'object' ? map : {};
    } catch (e) { return {}; }
  }

  function writeRejections(map) {
    try { sessionStorage.setItem(CODE_REJECTED_KEY, JSON.stringify(map)); } catch (e) {}
  }

  function isCodeRejected(cart, code) {
    const token = cartTokenOf(cart);
    if (!token) return false;
    return readRejections()[token + '|' + codeKey(code)] === cartStateSignature(cart);
  }

  // Only the current cart's entries are kept, so the map never grows past the codes of one cart.
  function markCodeRejected(cart, code) {
    const token = cartTokenOf(cart);
    if (!token || !codeKey(code)) return;
    const map = readRejections();
    Object.keys(map).forEach(function (k) { if (k.indexOf(token + '|') !== 0) delete map[k]; });
    map[token + '|' + codeKey(code)] = cartStateSignature(cart);
    writeRejections(map);
  }

  // Entering the code again is a new try, whatever the cart state.
  function clearCodeRejection(code) {
    const suffix = '|' + codeKey(code);
    const map = readRejections();
    Object.keys(map).forEach(function (k) {
      if (k.length >= suffix.length && k.slice(-suffix.length) === suffix) delete map[k];
    });
    writeRejections(map);
  }

  // Diagnosis for the cart state and the storefront tests: the gift line is already free through
  // another discount (the automatic "Free gift"), so Shopify had nothing left for the code.
  function giftFreeByOtherDiscount(cart, ruleId, code) {
    const k = codeKey(code);
    return ((cart && cart.items) || []).some(function (it) {
      if (!(it.properties && it.properties._mg_gift_rule === ruleId)) return false;
      const full = it.original_line_price || 0;
      return full > 0 && (it.line_level_discount_allocations || []).some(function (a) {
        const title = a && a.discount_application && a.discount_application.title;
        return codeKey(title) !== k && (a.amount || 0) >= full;
      });
    });
  }

  // Reads the answer to the codes tried in this pass. A code Shopify still does not apply loses its
  // gift again and is remembered as rejected for this cart state. Resolves to the final cart.
  function checkCodeTrials(cart, trials) {
    if (!trials.length) return Promise.resolve({ cart: cart, accepted: 0 });
    const appliedNow = {};
    (cart.discount_codes || []).forEach(function (c) {
      if (c && c.applicable === true) appliedNow[codeKey(c.code)] = true;
    });
    const removeKeys = [];
    let accepted = 0;
    const results = trials.map(function (entry) {
      const ok = !!appliedNow[codeKey(entry.trialCode)];
      const result = {
        code: entry.trialCode,
        ruleId: entry.ruleId,
        result: ok ? 'applied' : 'rejected',
        giftFreeByOtherDiscount: !ok && giftFreeByOtherDiscount(cart, entry.ruleId, entry.trialCode)
      };
      if (ok) { accepted++; return result; }
      markCodeRejected(cart, entry.trialCode);
      (cart.items || []).forEach(function (it) {
        if (it.properties && it.properties._mg_gift_rule === entry.ruleId && removeKeys.indexOf(it.key) === -1) removeKeys.push(it.key);
      });
      return result;
    });
    window.mgCI.codeTrials = results.concat(window.mgCI.codeTrials || []).slice(0, 10);
    if (!removeKeys.length) return Promise.resolve({ cart: cart, accepted: accepted });
    // One change at a time, as everywhere else on this cart.
    return removeKeys.reduce(function (chain, key) {
      return chain.then(function () { return postJSON('/cart/change.js', { id: key, quantity: 0 }); });
    }, Promise.resolve())
      .then(getCart)
      .then(function (fresh) { return { cart: fresh, accepted: accepted }; });
  }

  function isFreeLine(item) {
    return !!(item && item.properties && (item.properties._mg_gift || item.properties._mg_sample));
  }

  // Does the rule's condition hold for the current cart? Only paid lines count — a gift or
  // a sample must never pay for the next gift. Spend is compared against the line price
  // AFTER line discounts, in the currency of the market (the section already picked the
  // EU or the CH threshold).
  function ruleQualifies(rule, lines) {
    let spend = 0;
    let count = 0;
    lines.forEach(function (line) {
      if (line.gift || line.sample) return;
      if (rule.collections.length) {
        let hit = false;
        for (let i = 0; i < rule.collections.length; i++) {
          if (line.collections.indexOf(rule.collections[i]) !== -1) { hit = true; break; }
        }
        if (!hit) return;
      }
      spend += line.finalLinePrice || 0;
      count += line.quantity || 0;
    });
    if (rule.condition === 'count') return rule.count > 0 && count >= rule.count;
    return rule.threshold > 0 && spend >= rule.threshold;
  }

  // Rules that currently apply, in configuration order.
  //   - a promo-code rule needs its code in the cart, applied by Shopify (appliedCodes)
  //   - promo-code promotions never stack: the first one that applies wins. A later code rule
  //     whose code is applied and whose condition holds is listed in `stackBlocked`, so the
  //     cart can say why its gift does not come (MG-PATCH 2026-09-30)
  //   - a rule marked "not combinable" suppresses the threshold gifts while it applies
  //   - a rule whose gift is sold out does not apply at all (MG-PATCH 2026-09-27: it used to
  //     count as applying, so a sold-out exclusive rule took the threshold gifts out and gave
  //     nothing in return)
  //   - MG-PATCH 2026-10-13: a promo-code rule whose code is entered but not applied, and whose
  //     other conditions hold, is listed in `trials` (its gift is added on trial, see "rule codes:
  //     add the gift first"), or in `rejected` when this cart state already turned the code down.
  //     A rule on trial takes the one promo-code gift of the order, but does not take the threshold
  //     gifts out before Shopify has confirmed the code.
  function evaluateRules(cart, rules) {
    const result = { active: [], trials: [], rejected: [], suppressLadder: false, stackBlocked: [] };
    if (!rules || rules.enabled !== true || !rules.rules.length) return result;
    const codes = appliedCodes(cart, rules);
    const entered = enteredCodes(cart, rules);
    let codeRuleTaken = false;

    // MG-PATCH 2026-10-05 (CR-03): the section only lists a rule with a country list when the
    // country of the request is on it. The same check here keeps an island that is out of date
    // (or one the storefront tests extend) from giving a gift in another country.
    const market = String(rules.market || '').toUpperCase();

    rules.rules.forEach(function (rule) {
      if (rule.available === false) return;
      if (Array.isArray(rule.countries) && rule.countries.length && rule.countries.indexOf(market) === -1) return;
      const code = rule.activation === 'code' ? codeKey(rule.code) : '';
      if (rule.activation === 'code' && (!code || (!codes[code] && !entered[code]))) return;
      if (!ruleQualifies(rule, rules.lines)) return;
      const onTrial = rule.activation === 'code' && !codes[code];
      if (onTrial && isCodeRejected(cart, rule.code)) { result.rejected.push(rule); return; }
      if (rule.activation === 'code') {
        if (codeRuleTaken) { result.stackBlocked.push(rule); return; }
        codeRuleTaken = true;
      }
      if (onTrial) { result.trials.push(rule); return; }
      result.active.push(rule);
      if (!rule.combinable) result.suppressLadder = true;
    });
    return result;
  }

  // ─────────────────────────────────────────────
  //  MG-PATCH 2026-09-27 (CR-01, MGP test feedback 2 + 3) — promo codes of gift rules
  // ─────────────────────────────────────────────
  //
  // Shopify keeps a code on the cart even when it cannot apply it (expired, conditions not
  // met, not combinable with the automatic "Free gift" discount) and reports it with
  // `applicable: false`. The theme's coupon form treated that as a failure: it showed an error
  // and stopped before announcing the cart change. For a code that belongs to a gift rule the
  // form now closes without the error (assets/cart.js, MG-PATCH), and the cart shows the code
  // with a neutral note. Since 30.09. (strict mode, see appliedCodes) such a code brings no
  // gift; the note says that it could not be applied. Since 13.10. the code first gets one try
  // per cart state (gift in, Shopify decides), and the coupon form waits for that answer.
  //
  // Such a code has no discount application, so Sleek never lists it (its list is built from
  // the applications) and would drop it on the next coupon change, because it re-sends only
  // the codes it lists. The engine therefore lists these codes itself ([data-mg-rule-codes])
  // and hands them to Sleek (hiddenRuleCodes) whenever Sleek rewrites the code list.
  let lastCartCodes = [];
  let lastVerdict = null;
  let codeChangeInFlight = false;
  let codeCheckInFlight = false;

  function ruleByCode(code) {
    if (typeof code !== 'string' || !code.trim()) return null;
    const needle = code.trim().toLowerCase();
    const known = lastFreshRules || readRulesData();
    const list = (known && known.rules) || [];
    for (let i = 0; i < list.length; i++) {
      if (list[i].activation === 'code' && list[i].code && list[i].code.toLowerCase() === needle) return list[i];
    }
    return null;
  }

  // Codes on the cart that Shopify does not apply but a gift rule owns.
  function hiddenRuleCodes() {
    return lastCartCodes
      .filter(function (c) { return c && c.applicable === false && ruleByCode(c.code); })
      .map(function (c) { return c.code; });
  }

  function ruleStrings() {
    const known = lastFreshRules || readRulesData();
    return (known && known.strings) || {};
  }

  // All rules that hold this code. Two rules may share one code; the note speaks for the code.
  function rulesByCode(code) {
    const needle = typeof code === 'string' ? code.trim().toLowerCase() : '';
    const known = lastFreshRules || readRulesData();
    return ((known && known.rules) || []).filter(function (r) {
      return needle && r.activation === 'code' && r.code && r.code.toLowerCase() === needle;
    });
  }

  // What the cart says about one rule code (MG-PATCH 2026-09-30, strict mode; MG-PATCH 2026-10-13,
  // a code that Shopify does not apply gets one try per cart state):
  //   not-applied  the code was tried and Shopify did not apply it, even with the gift in the cart:
  //                no gift, neutral note
  //   added        a rule of this code applies, its gift is in the cart
  //   single       the code and its condition hold, but another code rule already gave the one
  //                promo-code gift of this order (code promotions never stack)
  //   pending      the condition does not hold yet (or the try is still running); the gift is
  //                added, and the code tried, once it holds
  // Every code that Shopify does not apply also gets a pill with a remove button, because Sleek
  // lists only applied codes.
  function ruleCodeState(entry) {
    const verdict = lastVerdict || {};
    const ids = function (list) { const m = {}; (list || []).forEach(function (r) { m[r.id] = true; }); return m; };
    const active = ids(verdict.active);
    const blocked = ids(verdict.stackBlocked);
    const rejected = ids(verdict.rejected);
    const rules = rulesByCode(entry.code);
    if (entry.applicable !== false && rules.some(function (r) { return active[r.id]; })) return 'added';
    if (rules.some(function (r) { return blocked[r.id]; })) return 'single';
    if (entry.applicable === false && rules.some(function (r) { return rejected[r.id]; })) return 'not-applied';
    return 'pending';
  }

  const RULE_CODE_STRING = { 'not-applied': 'codeNotApplied', added: 'codeAdded', single: 'codeOnePerOrder', pending: 'codePending' };

  // Draws the rule codes into every [data-mg-rule-codes] holder (cart drawer footer, cart page
  // subtotal). The holders come back empty with every Sleek re-render, so this runs after each
  // cart read. DOM is built from a server-rendered <template> (icons, translated labels) and
  // filled with textContent only. Every code of the cart that belongs to a rule gets a note;
  // only the codes Shopify does not apply get a pill of their own (Sleek lists the others).
  function renderRuleCodes() {
    const holders = document.querySelectorAll('[data-mg-rule-codes]');
    if (!holders.length) return;
    const tpl = document.getElementById('mg-rule-code-template');
    const strings = ruleStrings();
    const entries = [];
    const seen = {};
    if (rulesEnabled()) {
      lastCartCodes.forEach(function (c) {
        const code = typeof c === 'string' ? c : c && c.code;
        if (!code || seen[code.toLowerCase()] || !ruleByCode(code)) return;
        seen[code.toLowerCase()] = true;
        const entry = { code: code, applicable: typeof c === 'string' ? true : c.applicable !== false };
        entry.state = ruleCodeState(entry);
        entries.push(entry);
      });
    }
    const signature = entries.map(function (e) { return e.code + ':' + e.applicable + ':' + e.state; }).join('|');

    holders.forEach(function (holder) {
      if (holder.getAttribute('data-mg-signature') === signature) return;
      holder.setAttribute('data-mg-signature', signature);
      const list = holder.querySelector('[data-mg-rule-code-list]');
      const notes = holder.querySelector('[data-mg-rule-code-notes]');
      if (!list || !notes) return;
      while (list.firstChild) list.removeChild(list.firstChild);
      while (notes.firstChild) notes.removeChild(notes.firstChild);
      holder.hidden = !entries.length;
      list.hidden = !entries.some(function (e) { return e.applicable === false; });

      entries.forEach(function (entry) {
        if (entry.applicable === false && tpl && tpl.content && tpl.content.firstElementChild) {
          const pill = tpl.content.firstElementChild.cloneNode(true);
          const label = pill.querySelector('[data-mg-rule-code-label]');
          if (label) label.textContent = entry.code;
          const btn = pill.querySelector('[data-mg-rule-code-remove]');
          if (btn) {
            btn.setAttribute('data-mg-rule-code-remove', entry.code);
            btn.setAttribute('aria-label', String(btn.getAttribute('aria-label') || '').replace('__CODE__', entry.code));
          }
          list.appendChild(pill);
        }
        const note = document.createElement('p');
        note.className = 'mg-rule-codes__note text-sm';
        note.setAttribute('data-mg-rule-code-note', entry.state);
        note.textContent = String(strings[RULE_CODE_STRING[entry.state]] || '').replace('__CODE__', entry.code);
        notes.appendChild(note);
      });
    });
  }

  // Removing a rule code re-sends every other code of the cart. The Ajax API replaces the whole
  // code list with what it is given, so the full list comes from a fresh cart read.
  document.addEventListener('click', function (e) {
    const btn = e.target && e.target.closest ? e.target.closest('[data-mg-rule-code-remove]') : null;
    if (!btn) return;
    e.preventDefault();
    if (btn.getAttribute('aria-disabled') === 'true') return;
    const code = btn.getAttribute('data-mg-rule-code-remove');
    if (!code) return;
    btn.setAttribute('aria-disabled', 'true');
    btn.classList.add('btn--loading');
    codeChangeInFlight = true;
    getCart()
      .then(function (cart) {
        const keep = (cart.discount_codes || [])
          .map(function (c) { return typeof c === 'string' ? c : c && c.code; })
          .filter(function (c) { return c && c.toLowerCase() !== code.toLowerCase(); });
        return publishSleek(cart, 'mg-rule-code-remove', { discount: keep.join(',') });
      })
      .then(function () { codeChangeInFlight = false; })
      .catch(function (err) {
        codeChangeInFlight = false;
        btn.removeAttribute('aria-disabled');
        btn.classList.remove('btn--loading');
        console.error('[MG Cart Incentives] removing the promo code failed:', err);
      });
  });

  // MG-PATCH 2026-09-27 (CR-01, MGP test feedback 5): while a rule that is not combinable
  // applies, the threshold ladder (milestones, progress, "x/3 gifts") is hidden in the drawer
  // and on the cart page. The class on <html> survives Sleek's re-renders; the class on the
  // ladder itself matches what the server renders (snippets/mg-cart-gift-ladder.liquid) when
  // the exclusive rule gift is already in the cart, so the ladder does not flash on load.
  function applyLadderSuppression(on) {
    document.documentElement.classList.toggle('mg-ladder-suppressed', !!on);
    document.querySelectorAll('[data-mg-gift-ladder]').forEach(function (ladder) {
      ladder.classList.toggle('is-suppressed', !!on);
    });
  }

  // MG-PATCH 2026-07-03: session memory for deliberately removed gifts. The remove
  // control on gift lines is visible again (customer-care feedback: a shopper must
  // always be able to take a gift out) — without this flag the ladder would force
  // the gift straight back in on the next cart event. Cleared naturally with the
  // browser session.
  function giftDismissKey(vId) { return 'mg_gift_dismissed:' + String(vId); }
  function isGiftDismissed(vId) {
    try { return sessionStorage.getItem(giftDismissKey(vId)) === '1'; } catch (e) { return false; }
  }
  function markGiftDismissed(vId) {
    try { sessionStorage.setItem(giftDismissKey(vId), '1'); } catch (e) {}
  }
  // Delegated (capture) so it survives every drawer re-render: a click on the
  // remove control inside a gift line marks that variant as dismissed BEFORE
  // Sleek's cart-remove-item element fires the change request.
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    const removeCtl = e.target.closest('[is="cart-remove-item"], .cart-item__remove');
    if (!removeCtl) return;
    const line = removeCtl.closest('.mg-cart-line--gift');
    if (!line) return;
    const holder = line.hasAttribute('data-variant-id') ? line : line.querySelector('[data-variant-id], [data-quantity-variant-id]');
    const vId = holder && (holder.getAttribute('data-variant-id') || holder.getAttribute('data-quantity-variant-id'));
    if (vId) markGiftDismissed(vId);
  }, true);

  // Cart total in cents EXCLUDING free gift/sample lines, so they never count toward
  // tier thresholds or the progress bar. Subtract their FINAL line price (what is
  // actually included in cart.total_price), NOT original_line_price: on the cart page
  // the gift line is already 0 (discount applied), so subtracting the original €9 was
  // a double subtraction that pulled a cart just above a threshold (e.g. a single €84
  // item vs the €80 tier) back under it — the gift then add/removed itself in a loop.
  function qualifyingTotalCents(cart) {
    var t = (cart && cart.total_price) || 0;
    ((cart && cart.items) || []).forEach(function (it) {
      if (it.properties && (it.properties._mg_gift || it.properties._mg_sample)) {
        t -= (it.final_line_price || 0);
      }
    });
    return t < 0 ? 0 : t;
  }

  // Re-render Sleek's cart drawer / page after we mutate the cart.
  // CRITICAL: Sleek's onCartUpdate ignores any event whose cart has no `sections`
  // (cart.js: `if (typeof event.cart.sections === 'undefined') return;`). So a bare
  // publish never refreshes the drawer — our added/removed lines stay invisible.
  // We mirror Sleek's own flow: collect the section IDs via the `cart:grouped-sections`
  // event, ask the AJAX API to render them (update.js with `sections`), then publish
  // the cart WITH those sections so Sleek swaps the DOM. The passed `cart` is ignored
  // (we fetch the authoritative cart + rendered sections together).
  // MG-PATCH 2026-09-27 (CR-01): `extra` rides along in the same update.js call (used to rewrite
  // the discount codes), so a change and its re-render stay one request.
  function publishSleek(_cart, source, extra) {
    if (!(window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.publish && FoxTheme.pubsub.PUB_SUB_EVENTS)) {
      return extra ? postJSON('/cart/update.js', extra).then(function (r) { return r.json(); }) : Promise.resolve(_cart);
    }
    var secs = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: secs } })
    );
    secs = secs.filter(function (v, i, a) { return v && a.indexOf(v) === i; });
    var body = { sections: secs };
    if (extra) Object.keys(extra).forEach(function (k) { body[k] = extra[k]; });
    return postJSON('/cart/update.js', body)
      .then(function (r) {
        if (!r.ok) throw new Error('/cart/update.js answered ' + r.status);
        return r.json();
      })
      .then(function (cart) {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cart, source: source || 'mg-cart-incentives' });
        return cart;
      })
      .catch(function (err) {
        // A cart without sections is ignored by Sleek's renderer, and no cart at all would
        // break it, so the fallback publish only goes out with a real cart object.
        if (_cart) FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: _cart, source: source || 'mg-cart-incentives' });
        if (extra) throw err;
        return _cart;
      });
  }

  // ─────────────────────────────────────────────
  //  Gift Ladder — live progress
  // ─────────────────────────────────────────────

  // MG-PATCH 2026-10-02 (CR-02): takes the result of ladderAmounts(). The gift tiers and the bar
  // follow `gift`, the free-shipping milestone follows `shipping`. A plain number still works
  // (both amounts the same, no sale note).
  function refreshGiftLadders(amounts) {
    if (typeof amounts === 'number') amounts = { gift: amounts, shipping: amounts, saleLines: 0 };
    amounts = amounts || { gift: 0, shipping: 0, saleLines: 0 };
    window.mgCI.lastAmounts = amounts;
    const ladders = document.querySelectorAll('[data-mg-gift-ladder]');
    if (!ladders.length) return;

    const total = (amounts.gift || 0) / 100;
    const shippingTotal = (amounts.shipping || 0) / 100;

    ladders.forEach(function (ladder) {
      const tier1 = parseFloat(ladder.dataset.tier1) || 0;
      const tier2 = parseFloat(ladder.dataset.tier2) || 0;
      const tier3 = parseFloat(ladder.dataset.tier3) || 0;

      const fsEnabled = ladder.dataset.fsEnabled === 'true';
      const fsThreshold = parseFloat(ladder.dataset.fs) || 0;
      const fsReached = fsEnabled && shippingTotal >= fsThreshold;

      const t1 = total >= tier1;
      const t2 = total >= tier2;
      const t3 = total >= tier3;

      // Next UNREACHED milestone (free shipping first, then gift tiers).
      let nextTarget;
      let allUnlocked = false;
      let remainingBase = total;
      if (fsEnabled && !fsReached) {
        nextTarget = fsThreshold;
        remainingBase = shippingTotal;
      } else if (!t1) {
        nextTarget = tier1;
      } else if (!t2) {
        nextTarget = tier2;
      } else if (!t3) {
        nextTarget = tier3;
      } else {
        nextTarget = tier3;
        allUnlocked = true;
      }

      const remainingInt = Math.round(Math.max(0, nextTarget - remainingBase));

      // Continuous progress toward the highest tier (0 → tier3), monotonic.
      const progressPct = tier3 > 0 ? Math.max(0, Math.min(100, (total / tier3) * 100)) : 0;

      const fill = ladder.querySelector('[data-mg-gift-progress]');
      if (fill) fill.style.width = progressPct + '%';

      const remainingEl = ladder.querySelector('[data-mg-gift-remaining]');
      if (remainingEl) remainingEl.textContent = remainingInt;

      // Free-shipping milestone + gift tiers: only toggle .is-unlocked; the
      // truck/number → ✓ swap is pure CSS (no innerHTML, security-hook safe).
      const fsEl = ladder.querySelector('[data-mg-fs]');
      if (fsEl) fsEl.classList.toggle('is-unlocked', fsReached);

      ladder.querySelectorAll('[data-mg-tier]').forEach(function (tierEl) {
        const tierNum = parseInt(tierEl.dataset.mgTier, 10);
        const reached = (tierNum === 1 && t1) || (tierNum === 2 && t2) || (tierNum === 3 && t3);
        tierEl.classList.toggle('is-unlocked', reached);
      });

      // MG-PATCH 2026-09-27 (CR-01, MGP test feedback 5): the "x/3 gifts" note follows the same
      // total as the milestones. It used to be server-rendered only, from a total that had the
      // free lines subtracted at their original price, and read "1/3" next to three ticks.
      const unlocked = (t1 ? 1 : 0) + (t2 ? 1 : 0) + (t3 ? 1 : 0);
      const countEl = ladder.querySelector('[data-mg-gift-count]');
      if (countEl) countEl.textContent = String(unlocked);
      const noteEl = ladder.querySelector('[data-mg-gift-note]');
      if (noteEl) noteEl.hidden = unlocked === 0;
      const saleNoteEl = ladder.querySelector('[data-mg-gift-sale-note]');
      if (saleNoteEl) saleNoteEl.hidden = !(amounts.saleLines > 0);

      const wasAllUnlocked = ladder.dataset.allUnlocked === 'true';
      if (wasAllUnlocked !== allUnlocked) {
        ladder.dataset.allUnlocked = String(allUnlocked);
        ladder.classList.toggle('is-fully-unlocked', allUnlocked);
      }
    });
  }

  // ─────────────────────────────────────────────
  //  Gift auto-add (live mode only)
  // ─────────────────────────────────────────────

  let giftSyncInFlight = false;
  let readsInFlight = 0;

  // Returns { tiers: [{ amount, variant }] } for the visitor's market, or null
  // when live mode is off / no gift products configured (→ visual-only ladder).
  function collectGiftConfig() {
    const cfg = readConfig();
    if (!cfg || !isFreeItemsLive() || !Array.isArray(cfg.tiers) || !cfg.tiers.length) return null;

    const ladder = document.querySelector('[data-mg-gift-ladder]');
    const market = ladder ? (ladder.dataset.market || 'DE') : 'DE';
    const isCH = market === 'CH';

    const tiers = cfg.tiers
      .filter(function (t) { return t.variantId; })
      .map(function (t) { return { amount: isCH ? (t.ch || 0) : (t.eu || 0), variant: String(t.variantId) }; })
      .filter(function (t) { return t.amount > 0; });

    return tiers.length ? { tiers: tiers } : null;
  }

  // MG-PATCH 2026-09-22 (CR-01 / INC0102718): one reconcile pass for every free line in
  // the cart — threshold gifts, gift-rule gifts and samples. They used to be handled in
  // separate passes, which let them fight over the same cart while a mutation was still
  // in flight. The pass builds ONE plan (add / remove / clamp / clear) and then executes
  // it, so the cart is touched once per event.
  // MG-PATCH 2026-10-13 (CR-01): returns a promise of the pass ({ changed, cart } or null when there
  // was nothing to do) and plans the promo-code tries. `opts.deferPublish` leaves the re-render to the
  // caller (the coupon form, which closes its dialog first).
  function syncFreeItems(cart, rules, rulesStale, opts) {
    if (giftSyncInFlight) return Promise.resolve(null);

    const cfg = readConfig();
    const items = cart.items || [];
    const plan = { add: [], remove: [], capQty: [], clearSamples: false, reject: [] };

    const freeLines = items.filter(isFreeLine);
    const paidLines = items.filter(function (it) { return !isFreeLine(it); });

    // INC0102718 (1): the shopper removed the last paid product. Gifts and samples earn
    // their place through a purchase, so nothing is left to earn them — a cart of free
    // items only would otherwise go to checkout at 0 EUR.
    // INC0102718 (2): the same when live mode has been switched off. The 100%-off
    // discount is then no longer guaranteed, so a leftover free line would be CHARGED.
    if (freeLines.length && (!paidLines.length || !isFreeItemsLive())) {
      freeLines.forEach(function (it) { plan.remove.push(it.key); });
      plan.clearSamples = true;
      return applyFreeItemPlan(plan, opts, rules);
    }
    if (!isFreeItemsLive()) return Promise.resolve(null);

    // INC0102718 (2), samples: a tester that the picker no longer offers cannot be
    // removed by the shopper (the remove control is hidden on sample lines) and is not
    // covered by the sample discount any more. Same failure mode as the stale tier gift
    // below. Skipped entirely when the configured list is unknown (null).
    const sampleIds = cfg && cfg.sample_product_ids;
    if (Array.isArray(sampleIds) && sampleIds.length) {
      const allowed = {};
      sampleIds.forEach(function (id) { allowed[String(id)] = true; });
      items.forEach(function (it) {
        if (it.properties && it.properties._mg_sample && !allowed[String(it.product_id)]) {
          plan.remove.push(it.key);
        }
      });
    }

    // ── Gift rules (CR-01) ───────────────────────────────────────────────
    // Without fresh rule data no rule gift is added or taken out; the ladder keeps out of
    // the way of an exclusive rule gift that is already in the cart.
    const verdict = rulesStale ? { active: [], trials: [], rejected: [], suppressLadder: false } : evaluateRules(cart, rules);
    const wantedRules = {}; // ruleId → rule
    const trialRules = {}; // ruleId → rule whose code Shopify does not apply yet (MG-PATCH 2026-10-13)
    verdict.active.forEach(function (rule) { wantedRules[rule.id] = rule; });
    verdict.trials.forEach(function (rule) { wantedRules[rule.id] = rule; trialRules[rule.id] = rule; });
    const ruleLinePresent = {};

    items.forEach(function (it) {
      const ruleId = it.properties && it.properties._mg_gift_rule;
      if (!ruleId) return;
      if (rulesStale) {
        if (ruleIsExclusive(ruleId)) verdict.suppressLadder = true;
        if ((it.quantity || 1) > 1) plan.capQty.push(it.key);
        return;
      }
      const rule = wantedRules[ruleId];
      const vId = String(it.variant_id || it.id);
      // Rule switched off, out of its date window, out of this market, condition no
      // longer met, or the gift product was swapped → the line goes.
      if (!rule || String(rule.variantId) !== vId || ruleLinePresent[ruleId]) {
        plan.remove.push(it.key);
        return;
      }
      ruleLinePresent[ruleId] = true;
      // MG-PATCH 2026-10-13 (CR-01): the gift of a code on trial is in the cart, and Shopify still does
      // not apply the code. That is Shopify's answer: the gift goes, the code is rejected for this
      // cart state. The try reads this answer itself; this catches a try whose check never finished
      // (page left mid-way, a read that failed).
      if (trialRules[ruleId]) {
        plan.remove.push(it.key);
        plan.reject.push(rule.code);
        return;
      }
      if ((it.quantity || 1) > 1) plan.capQty.push(it.key); // one gift per rule
    });

    verdict.active.forEach(function (rule) {
      if (ruleLinePresent[rule.id]) return;
      if (isGiftDismissed(rule.variantId)) return;
      plan.add.push({ variantId: rule.variantId, ruleId: rule.id, handle: rule.handle, available: rule.available });
    });

    // MG-PATCH 2026-10-13 (CR-01): the try. A gift the shopper took out stays out, as for every rule.
    verdict.trials.forEach(function (rule) {
      if (ruleLinePresent[rule.id]) return;
      if (isGiftDismissed(rule.variantId)) return;
      plan.add.push({ variantId: rule.variantId, ruleId: rule.id, handle: rule.handle, available: rule.available, trialCode: rule.code });
    });

    // ── Threshold gifts (the launch ladder) ──────────────────────────────
    const ladder = collectGiftConfig();
    // MG-PATCH 2026-10-02 (CR-02): the tiers count the non-sale products only (ladderAmounts).
    // While the compare-at price of a line is unknown (no fresh section yet), the threshold gifts
    // stay as they are; the delayed re-read decides.
    const amounts = ladderAmounts(cart);
    if (ladder && !amounts.unknown) {
      const total = amounts.gift / 100;
      const wanted = {}; // variantId(string) → reached(bool)
      // A rule marked "not combinable" takes the whole ladder out while it applies.
      ladder.tiers.forEach(function (t) { wanted[t.variant] = !verdict.suppressLadder && total >= t.amount; });

      const presentVariants = {};
      items.forEach(function (it) {
        const isGift = it.properties && it.properties._mg_gift;
        if (!isGift) return;
        if (it.properties._mg_gift_rule) return; // owned by a gift rule, handled above
        const vId = String(it.variant_id || it.id);
        if (!wanted.hasOwnProperty(vId)) {
          // MG-PATCH 2026-07-03: STALE gift from an earlier tier configuration
          // (e.g. 661-20ml after a gift swap). It is no longer covered by the
          // Free-gift discount, so Shopify CHARGES it while the line renders as
          // a locked gift — the customer can neither see why nor remove it
          // (Trello dVJT3BQG/aO9LRWko, order 73535 paid €46 for one). Remove it;
          // the ladder re-adds the CURRENT tier gift right after if earned.
          plan.remove.push(it.key);
          return;
        }
        if (!wanted[vId]) {
          plan.remove.push(it.key); // below threshold, or suppressed by a rule → remove gift
        } else if (presentVariants[vId]) {
          plan.remove.push(it.key); // duplicate of an already-present gift → drop the extra
        } else {
          presentVariants[vId] = it; // keep the first qualifying gift line
          // MG-PATCH 2026-07-03: gifts are 1 unit per tier. The qty stepper is
          // hidden, but a merged /cart/add or a crafted change can still raise the
          // line quantity — and the 100%-off discount has no per-line cap, so every
          // extra unit ships free (order 73535: 4x tier-1 gift at €0). Clamp to 1.
          if ((it.quantity || 1) > 1) plan.capQty.push(it.key);
        }
      });

      Object.keys(wanted).forEach(function (vId) {
        // MG-PATCH 2026-07-03: respect a deliberate removal — once the shopper
        // takes a gift out (remove control is visible again per customer-care
        // feedback), do not force it back in for the rest of the session.
        if (wanted[vId] && !presentVariants[vId] && !isGiftDismissed(vId)) {
          plan.add.push({ variantId: vId, ruleId: null });
        }
      });
    }

    return applyFreeItemPlan(plan, opts, rules);
  }

  // MG-PATCH 2026-10-13 (CR-01): returns a promise of { changed, cart }. `changed` is false when the
  // pass left the cart as Sleek rendered it (the only mutation was a code try that Shopify turned down,
  // gift in and out again); such a pass is not re-rendered, which would also close the coupon dialog
  // that shows the error.
  function applyFreeItemPlan(plan, opts, rules) {
    opts = opts || {};
    plan.reject = plan.reject || [];
    if (!plan.add.length && !plan.remove.length && !plan.capQty.length && !plan.clearSamples) return Promise.resolve(null);

    giftSyncInFlight = true;
    const tried = []; // code tries whose gift add went out in this pass
    let plainAdds = 0;

    // "While stocks last": a rule gift is only added when its variant is really in stock.
    // Threshold gifts keep their launch behaviour (they are oversell-enabled on purpose).
    const gated = plan.add.map(function (entry) {
      if (!entry.ruleId) return Promise.resolve(entry);
      if (entry.available === false) return Promise.resolve(null);
      return variantAvailable(entry.handle, entry.variantId).then(function (ok) {
        // null = the product JSON could not answer; the section's flag (already known to be
        // true here) stands, so an unlisted gift product does not silently kill the rule.
        return ok === false ? null : entry;
      });
    });

    return Promise.all(gated)
      .then(function (entries) {
        // `_mg_gift` flags the line as a free gift (fulfillment/SAP identifier + the
        // 100%-off automatic discount targets it). `_mg_gift_rule` additionally records
        // WHICH rule put it there, so the rule can take its own gift back out without
        // touching the threshold gifts. Both are hidden properties (leading underscore).
        // One add at a time: parallel adds on the same cart are turned away with 409.
        return entries.filter(Boolean).reduce(function (chain, entry) {
          return chain.then(function () {
            const properties = { _mg_gift: 'true' };
            if (entry.ruleId) properties._mg_gift_rule = entry.ruleId;
            if (entry.trialCode) tried.push(entry); else plainAdds++;
            return postJSON('/cart/add.js', { id: Number(entry.variantId), quantity: 1, properties: properties });
          });
        }, Promise.resolve());
      })
      .then(function () {
        // Removals after adds resolve (keys can shift); sequential by key.
        return plan.remove.reduce(function (chain, key) {
          return chain.then(function () { return postJSON('/cart/change.js', { id: key, quantity: 0 }); });
        }, Promise.resolve());
      })
      .then(function () {
        // MG-PATCH 2026-07-03: clamp over-quantified gift lines back to 1 unit.
        return plan.capQty.reduce(function (chain, key) {
          return chain.then(function () { return postJSON('/cart/change.js', { id: key, quantity: 1 }); });
        }, Promise.resolve());
      })
      .then(function () {
        // Keep the SAP pick-list note truthful once the sample lines are gone.
        if (!plan.clearSamples) return null;
        return postJSON('/cart/update.js', { attributes: { samples_selection: '' } });
      })
      .then(getCart)
      // MG-PATCH 2026-10-13 (CR-01): Shopify's answer to the code tries, read before anything is
      // re-rendered, so a turned-down try never shows.
      .then(function (fresh) { return checkCodeTrials(fresh, tried); })
      .then(function (outcome) {
        const fresh = outcome.cart;
        plan.reject.forEach(function (code) { markCodeRejected(fresh, code); });
        giftSyncInFlight = false;
        refreshGiftLadders(ladderAmounts(fresh));
        document.querySelectorAll('[data-mg-sample-picker]').forEach(function (p) {
          if (typeof p.__mgApplyCartState === 'function') p.__mgApplyCartState(fresh);
        });
        // The rule-code notes follow the answer right away; they live outside Sleek's re-render.
        if (Array.isArray(fresh.discount_codes)) lastCartCodes = fresh.discount_codes;
        if (rules && rulesEnabled()) {
          lastVerdict = evaluateRules(fresh, rules);
          window.mgCI.lastVerdict = lastVerdict;
        }
        renderRuleCodes();
        const changed = plainAdds > 0 || outcome.accepted > 0 || plan.remove.length > 0 || plan.capQty.length > 0 || plan.clearSamples;
        if (changed && !opts.deferPublish) publishSleek(fresh, 'mg-gift-ladder');
        return { changed: changed, cart: fresh };
      })
      .catch(function (err) {
        giftSyncInFlight = false;
        console.error('[MG Cart Incentives] free-item reconcile failed:', err);
        return null;
      });
  }
/*
  // ─────────────────────────────────────────────
  //  Sample Picker OLD
  // ─────────────────────────────────────────────

  // Serialise all sample cart mutations across every picker instance so rapid
  // toggles can't race on cart line keys. `samplesBusy` suppresses cart-driven
  // re-checks while a mutation is mid-flight (avoids flicker from interleaved
  // pubsub events firing with a stale cart).
  let sampleQueue = Promise.resolve();
  let samplesBusy = 0;

  function sampleProductIdsInCart(cart) {
    const set = {};
    (cart.items || []).forEach(function (it) {
      if (it.properties && it.properties._mg_sample) set[String(it.product_id)] = it;
    });
    return set;
  }

  function bindSamplePicker(picker) {
    const quota = parseInt(picker.dataset.quota, 10) || 6;
    const checkboxes = Array.prototype.slice.call(picker.querySelectorAll('.mg-sample-card__checkbox'));
    const counter = picker.querySelector('[data-mg-sample-selected-count]');
    const hint = picker.querySelector('[data-mg-sample-hint]');
    const limit = picker.querySelector('[data-mg-sample-limit]');

    function checked() { return checkboxes.filter(function (cb) { return cb.checked; }); }

    function syncUi() {
      const count = checked().length;
      if (counter) counter.textContent = String(count);

      checkboxes.forEach(function (cb) {
        // Leave OOS/disabled-elsewhere boxes alone; only manage quota-locking.
        if (cb.disabled && cb.dataset.mgDisabledByPicker !== '1') {
          // disabled for another reason (out of stock) — skip quota logic
        } else if (!cb.checked && count >= quota) {
          cb.disabled = true; cb.dataset.mgDisabledByPicker = '1';
        } else if (cb.dataset.mgDisabledByPicker === '1') {
          cb.disabled = false; delete cb.dataset.mgDisabledByPicker;
        }
        const card = cb.closest('[data-mg-sample-card]');
        if (card) card.classList.toggle('is-selected', cb.checked);
      });

      if (hint) hint.hidden = count >= quota;
      if (limit) limit.hidden = count < quota;
    }

    // In LIVE mode, reflect the real cart: a checkbox is checked iff its product
    // is present as a tagged sample line. In display mode the server-rendered
    // `checked` state (from the cart attribute) stands.
    function applyCartState(cart) {
      if (samplesBusy > 0) { syncUi(); return; } // a mutation is in flight; don't fight it
      if (!isFreeItemsLive() || !cart || !Array.isArray(cart.items)) { syncUi(); return; }
      const present = sampleProductIdsInCart(cart);
      checkboxes.forEach(function (cb) { cb.checked = !!present[cb.value]; });
      syncUi();
    }
    picker.__mgApplyCartState = applyCartState;

    // Comma-separated product IDs of currently checked samples → cart note.
    function writeAttribute() {
      const ids = checked().map(function (cb) { return cb.value; }).join(',');
      return postJSON('/cart/update.js', { attributes: { samples_selection: ids } })
        .then(function (r) { return r.json(); });
    }

    function mirrorDisplayMode() {
      const ids = checked().map(function (cb) { return cb.value; });
      document.querySelectorAll('[data-mg-sample-picker]').forEach(function (other) {
        if (other === picker) return;
        other.querySelectorAll('.mg-sample-card__checkbox').forEach(function (otherCb) {
          otherCb.checked = ids.indexOf(otherCb.value) !== -1;
        });
        if (other.__mgPickerInstance && typeof other.__mgPickerInstance.syncUi === 'function') {
          other.__mgPickerInstance.syncUi();
        }
      });
    }

    function reconcileAllPickers(cart) {
      document.querySelectorAll('[data-mg-sample-picker]').forEach(function (p) {
        if (typeof p.__mgApplyCartState === 'function') p.__mgApplyCartState(cart);
      });
    }

    function onChange(cb) {
      syncUi(); // optimistic — instant feedback regardless of mode

      if (!isFreeItemsLive()) {
        // DISPLAY MODE: persist the selection as a cart note only.
        writeAttribute().then(function (cart) {
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
        });
        mirrorDisplayMode();
        return;
      }

      // LIVE MODE: add/remove the real sample line, serialised.
      const wantChecked = cb.checked;
      const productId = cb.value;
      const variantId = cb.dataset.variantId;

      samplesBusy++;
      sampleQueue = sampleQueue
        .then(getCart)
        .then(function (cart) {
          const present = sampleProductIdsInCart(cart);
          if (wantChecked && !present[productId]) {
            return postJSON('/cart/add.js', {
              id: Number(variantId), quantity: 1, properties: { _mg_sample: 'true' }
            });
          }
          if (!wantChecked && present[productId]) {
            return postJSON('/cart/change.js', { id: present[productId].key, quantity: 0 });
          }
          return null; // already in desired state
        })
        .then(getCart)
        .then(function (fresh) {
          // Keep the SAP summary note in sync with the real sample lines.
          const ids = Object.keys(sampleProductIdsInCart(fresh));
          return postJSON('/cart/update.js', { attributes: { samples_selection: ids.join(',') } })
            .then(function () { return fresh; });
        })
        .then(function (fresh) {
          samplesBusy = Math.max(0, samplesBusy - 1);
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: fresh }));
          refreshGiftLadders(ladderAmounts(fresh));
          reconcileAllPickers(fresh);
          publishSleek(fresh, 'mg-sample-picker');
          return fresh;
        })
        .catch(function (err) {
          samplesBusy = Math.max(0, samplesBusy - 1);
          console.error('[MG Sample Picker] cart update failed:', err);
          // Re-sync from the server so the UI never lies about cart contents.
          return getCart().then(reconcileAllPickers).catch(function () {});
        });
    }

    checkboxes.forEach(function (cb) {
      cb.addEventListener('change', function () { onChange(cb); });
    });

    picker.__mgPickerInstance = { syncUi: syncUi, applyCartState: applyCartState };
    syncUi();
  }
*/

// ─────────────────────────────────────────────
//  Sample Picker KFIT - NEW 2026-09-18
// ─────────────────────────────────────────────

// Serialise all sample cart mutations across every picker instance so rapid
// toggles can't race on cart line keys. `samplesBusy` suppresses cart-driven
// re-checks while a mutation is mid-flight (avoids flicker from interleaved
// pubsub events firing with a stale cart).
let sampleQueue = Promise.resolve();
let samplesBusy = 0;

// ── Eligibility rule (updated 2026-10-01) ────────────────────
// The cart qualifies if it contains at least one regular product.
// Sample lines themselves (tagged _mg_sample) never count, so the samples
// can't keep themselves eligible once the regular products are gone.
// Gift lines the engine added (tagged _mg_gift, ladder and CR-01 rule gifts)
// don't count either. The decision is made per line, never per product ID:
// a gift product the shopper buys as a normal, paid product qualifies.
function isEligibleForSamples(cart) {
  if (!cart || !Array.isArray(cart.items)) return false;

  return cart.items.some(function (item) {
    if (item.properties && item.properties._mg_sample) return false; // sample lines
    if (item.properties && item.properties._mg_gift) return false;   // gift lines added by the engine
    return true;                                                     // a real qualifying product
  });
}

function sampleProductIdsInCart(cart) {
  const set = {};
  (cart.items || []).forEach(function (it) {
    if (it.properties && it.properties._mg_sample) set[String(it.product_id)] = it;
  });
  return set;
}

// ── Eligibility cleanup ──────────────────────────────────────
// Runs on EVERY cart update, including ones caused by other cart code
// (e.g. removing a qualifying product). If the cart no longer qualifies,
// removes all tagged sample lines and clears the selection attribute.
// Idempotent: once the samples are gone it returns without doing anything,
// so the follow-up cart:updated it dispatches is harmless.
function enforceSampleEligibility(cart) {
  if (samplesBusy > 0) return Promise.resolve(cart); // a picker mutation is in flight; it reconciles at the end
  // MG-PATCH 2026-09-23 (CR-01): the free-item reconcile owns the cart while it runs and hands
  // the fresh cart back to the pickers when it is done, so a second removal chain on the same
  // line keys is not started here.
  if (giftSyncInFlight) return Promise.resolve(cart);
  if (!cart || !Array.isArray(cart.items)) return Promise.resolve(cart);

  if (!isFreeItemsLive()) {
    // DISPLAY MODE: there are no real sample lines, but the persisted
    // selection attribute must not survive lost eligibility either.
    if (isEligibleForSamples(cart)) return Promise.resolve(cart);
    return postJSON('/cart/update.js', { attributes: { samples_selection: '' } })
      .then(getCart)
      .then(function (fresh) {
        document.querySelectorAll('[data-mg-sample-picker]').forEach(function (p) {
          p.querySelectorAll('.mg-sample-card__checkbox').forEach(function (cb) {
            cb.checked = false;
          });
          if (p.__mgPickerInstance && typeof p.__mgPickerInstance.syncUi === 'function') {
            p.__mgPickerInstance.syncUi();
          }
        });
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: fresh }));
        return fresh;
      })
      .catch(function (err) {
        console.error('[MG Sample Picker] display-mode cleanup failed:', err);
        return cart;
      });
  }

  // LIVE MODE: remove the real tagged sample lines.
  const tagged = sampleProductIdsInCart(cart);
  if (!Object.keys(tagged).length || isEligibleForSamples(cart)) {
    return Promise.resolve(cart);
  }

  samplesBusy++;
  return sampleQueue
    .then(function () {
      // One change at a time: parallel changes on the same cart are turned away with 409.
      return Object.keys(tagged).reduce(function (chain, productId) {
        return chain.then(function () {
          return postJSON('/cart/change.js', { id: tagged[productId].key, quantity: 0 });
        });
      }, Promise.resolve());
    })
    .then(function () {
      return postJSON('/cart/update.js', { attributes: { samples_selection: '' } });
    })
    .then(getCart)
    .then(function (fresh) {
      samplesBusy = Math.max(0, samplesBusy - 1);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: fresh }));
      refreshGiftLadders(ladderAmounts(fresh));
      reconcileAllPickers(fresh);
      publishSleek(fresh, 'mg-sample-picker');
      return fresh;
    })
    .catch(function (err) {
      samplesBusy = Math.max(0, samplesBusy - 1);
      console.error('[MG Sample Picker] eligibility cleanup failed:', err);
      return getCart().then(reconcileAllPickers).catch(function () {});
    });
}

// reconcileAllPickers is at module scope (it was nested inside bindSamplePicker
// before) so the cleanup routine can call it too.
function reconcileAllPickers(cart) {
  document.querySelectorAll('[data-mg-sample-picker]').forEach(function (p) {
    if (typeof p.__mgApplyCartState === 'function') p.__mgApplyCartState(cart);
  });
}

function bindSamplePicker(picker) {
  const quota = parseInt(picker.dataset.quota, 10) || 6;
  const checkboxes = Array.prototype.slice.call(picker.querySelectorAll('.mg-sample-card__checkbox'));
  const counter = picker.querySelector('[data-mg-sample-selected-count]');
  const hint = picker.querySelector('[data-mg-sample-hint]');
  const limit = picker.querySelector('[data-mg-sample-limit]');

  function checked() { return checkboxes.filter(function (cb) { return cb.checked; }); }

  function syncUi() {
    const count = checked().length;
    if (counter) counter.textContent = String(count);

    checkboxes.forEach(function (cb) {
      // Leave OOS/disabled-elsewhere boxes alone; only manage quota-locking.
      if (cb.disabled && cb.dataset.mgDisabledByPicker !== '1') {
        // disabled for another reason (out of stock) — skip quota logic
      } else if (!cb.checked && count >= quota) {
        cb.disabled = true; cb.dataset.mgDisabledByPicker = '1';
      } else if (cb.dataset.mgDisabledByPicker === '1') {
        cb.disabled = false; delete cb.dataset.mgDisabledByPicker;
      }
      const card = cb.closest('[data-mg-sample-card]');
      if (card) card.classList.toggle('is-selected', cb.checked);
    });

    if (hint) hint.hidden = count >= quota;
    if (limit) limit.hidden = count < quota;
  }

  // In LIVE mode, reflect the real cart: a checkbox is checked iff its product
  // is present as a tagged sample line. In display mode the server-rendered
  // `checked` state (from the cart attribute) stands.
  function applyCartState(cart) {
    if (samplesBusy > 0) { syncUi(); return; } // a mutation is in flight; don't fight it
    if (!cart || !Array.isArray(cart.items)) { syncUi(); return; }

    if (isFreeItemsLive()) {
      const present = sampleProductIdsInCart(cart);
      checkboxes.forEach(function (cb) { cb.checked = !!present[cb.value]; });
      syncUi();
      // NEW: revalidate eligibility on cart updates from anywhere —
      // including ones that removed the qualifying products.
      enforceSampleEligibility(cart);
      return;
    }

    // DISPLAY MODE: server-rendered state stands.
    syncUi();
    enforceSampleEligibility(cart);
  }
  picker.__mgApplyCartState = applyCartState;

  // Comma-separated product IDs of currently checked samples → cart attribute.
  function writeAttribute() {
    const ids = checked().map(function (cb) { return cb.value; }).join(',');
    return postJSON('/cart/update.js', { attributes: { samples_selection: ids } })
      .then(function (r) { return r.json(); });
  }

  function mirrorDisplayMode() {
    const ids = checked().map(function (cb) { return cb.value; });
    document.querySelectorAll('[data-mg-sample-picker]').forEach(function (other) {
      if (other === picker) return;
      other.querySelectorAll('.mg-sample-card__checkbox').forEach(function (otherCb) {
        otherCb.checked = ids.indexOf(otherCb.value) !== -1;
      });
      if (other.__mgPickerInstance && typeof other.__mgPickerInstance.syncUi === 'function') {
        other.__mgPickerInstance.syncUi();
      }
    });
  }

  function onChange(cb) {
    syncUi(); // optimistic — instant feedback regardless of mode

    if (!isFreeItemsLive()) {
      // DISPLAY MODE: persist the selection as a cart attribute only.
      writeAttribute().then(function (cart) {
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      });
      mirrorDisplayMode();
      return;
    }

    // LIVE MODE: add/remove the real sample line, serialised.
    const wantChecked = cb.checked;
    const productId = cb.value;
    const variantId = cb.dataset.variantId;

    samplesBusy++;
    sampleQueue = sampleQueue
      .then(getCart)
      .then(function (cart) {
        const present = sampleProductIdsInCart(cart);
        if (wantChecked && !present[productId]) {
          return postJSON('/cart/add.js', {
            id: Number(variantId), quantity: 1, properties: { _mg_sample: 'true' }
          });
        }
        if (!wantChecked && present[productId]) {
          return postJSON('/cart/change.js', { id: present[productId].key, quantity: 0 });
        }
        return null; // already in desired state
      })
      .then(getCart)
      .then(function (fresh) {
        // Keep the SAP summary attribute in sync with the real sample lines.
        const ids = Object.keys(sampleProductIdsInCart(fresh));
        return postJSON('/cart/update.js', { attributes: { samples_selection: ids.join(',') } })
          .then(function () { return fresh; });
      })
      .then(function (fresh) {
        samplesBusy = Math.max(0, samplesBusy - 1);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: fresh }));
        refreshGiftLadders(ladderAmounts(fresh));
        reconcileAllPickers(fresh);
        publishSleek(fresh, 'mg-sample-picker');
        return fresh;
      })
      .catch(function (err) {
        samplesBusy = Math.max(0, samplesBusy - 1);
        console.error('[MG Sample Picker] cart update failed:', err);
        // Re-sync from the server so the UI never lies about cart contents.
        return getCart().then(reconcileAllPickers).catch(function () {});
      });
  }

  checkboxes.forEach(function (cb) {
    cb.addEventListener('change', function () { onChange(cb); });
  });

  // enforceSampleEligibility is exposed on the picker instance so a central
  // cart:updated handler can also call it (safe to run twice).
  picker.__mgPickerInstance = { syncUi: syncUi, applyCartState: applyCartState, enforceSampleEligibility: enforceSampleEligibility };
  syncUi();
}

  // ─────────────────────────────────────────────
  //  Boot / cart-change handling
  // ─────────────────────────────────────────────

  // MG-PATCH 2026-07-02: remove crafted lines of hidden samples/gifts.
  // The cart Liquid flags any mg-hidden-tagged line WITHOUT a _mg_gift/_mg_sample
  // property as data-mg-illegit="<line key>" (a customer ordered free samples via
  // a bare /cart/add). CSS hides the row instantly; this sweep deletes it.
  let illegitSweepInFlight = false;
  function sweepIllegitLines() {
    if (illegitSweepInFlight) return;
    const keys = [];
    document.querySelectorAll('[data-mg-illegit]').forEach(function (el) {
      const k = el.getAttribute('data-mg-illegit');
      if (k && keys.indexOf(k) === -1) keys.push(k);
    });
    if (!keys.length) return;
    illegitSweepInFlight = true;
    keys.reduce(function (chain, key) {
      return chain.then(function () { return postJSON('/cart/change.js', { id: key, quantity: 0 }); });
    }, Promise.resolve())
      .then(getCart)
      .then(function (fresh) {
        illegitSweepInFlight = false;
        publishSleek(fresh, 'mg-illegit-sweep');
      })
      .catch(function () { illegitSweepInFlight = false; });
  }

  // MG-PATCH 2026-09-23 (CR-01): a read without fresh rule data leaves the rule gifts as they
  // are; a delayed re-read catches up. Capped, so a section that cannot render at all does not
  // turn into polling — the next cart event starts over.
  let rulesRetryTimer = null;
  let rulesRetries = 0;
  function retryWithFreshRules() {
    if (rulesRetryTimer || rulesRetries >= 2) return;
    rulesRetries++;
    rulesRetryTimer = setTimeout(function () {
      rulesRetryTimer = null;
      handleCart();
    }, 1500);
  }

  // MG-PATCH 2026-10-13 (CR-01): returns a promise of { ctx, result } once the reconcile pass of this
  // read has finished (verifyRuleCode waits on it); `opts` goes through to syncFreeItems.
  function handleCart(cart, opts) {
    window.mgCI.events++;
    // Always read a FRESH cart: pubsub payloads can omit line-item properties, which
    // breaks gift detection (remove below threshold) and sample reconciliation.
    // MG-PATCH 2026-09-22 (CR-01): with gift rules on, the same read also brings the
    // freshly rendered rules section (collection membership + applied discount codes),
    // so this stays ONE request either way.
    const read = islandNeeded()
      ? getCartWithRules()
      : getCart().then(function (c) { return { cart: c, rules: null }; });

    readsInFlight++;
    return read.then(function (ctx) {
      readsInFlight = Math.max(0, readsInFlight - 1);
      const c = ctx.cart;
      window.mgCI.lastTotal = c.total_price;
      window.mgCI.lastRules = ctx.rules || window.mgCI.lastRules;
      // MG-PATCH 2026-09-27 (CR-01): ladder visibility and the rule-code list follow the same
      // verdict the reconcile acts on. Without fresh rule data the last verdict stands.
      if (Array.isArray(c.discount_codes)) lastCartCodes = c.discount_codes;
      if (!rulesEnabled()) lastVerdict = { active: [], suppressLadder: false };
      else if (ctx.rulesStale !== true) lastVerdict = evaluateRules(c, ctx.rules);
      window.mgCI.lastVerdict = lastVerdict;
      applyLadderSuppression(!!(lastVerdict && lastVerdict.suppressLadder));
      renderRuleCodes();
      refreshGiftLadders(ladderAmounts(c));
      const pass = syncFreeItems(c, ctx.rules, ctx.rulesStale === true, opts);
      if (ctx.rulesStale === true) retryWithFreshRules(); else rulesRetries = 0;
      sweepIllegitLines();
      document.querySelectorAll('[data-mg-sample-picker]').forEach(function (p) {
        if (typeof p.__mgApplyCartState === 'function') p.__mgApplyCartState(c);
      });
      return pass.then(function (result) { return { ctx: ctx, result: result }; });
    }).catch(function () { readsInFlight = Math.max(0, readsInFlight - 1); return null; });
  }

  // ─────────────────────────────────────────────
  //  MG-PATCH 2026-10-13 (CR-01) — the coupon form asks for a rule code
  // ─────────────────────────────────────────────
  //
  // The coupon form (assets/cart.js, MG-PATCH) has just sent a code that belongs to a gift rule and
  // that Shopify does not apply. Entering it is a new try: an earlier rejection of this code is
  // forgotten, and so is a deliberate removal of its gift. The engine waits until no other read or
  // pass is running (one mutation at a time on this cart), runs one pass without re-rendering, and
  // answers what became of the code:
  //   applied   Shopify applies the code now; the form closes its dialog and re-renders the cart
  //   rejected  tried with the gift in the cart, Shopify did not apply it; the form shows its error
  //   pending   the rule's condition does not hold yet; the code stays on the cart for later
  //   single    another promo code already gave the one promo-code gift of this order
  //   unknown   no fresh rule data; the form carries on as before (no error)
  function whenIdle() {
    return new Promise(function (resolve) {
      (function check(round) {
        if ((!giftSyncInFlight && readsInFlight === 0) || round >= 150) { resolve(); return; }
        setTimeout(function () { check(round + 1); }, 100);
      })(0);
    });
  }

  function codeOutcome(code, out) {
    if (!out || !out.ctx || out.ctx.rulesStale === true) return 'unknown';
    const cart = (out.result && out.result.cart) || out.ctx.cart;
    const k = codeKey(code);
    const entry = ((cart && cart.discount_codes) || []).filter(function (c) { return c && codeKey(c.code) === k; })[0];
    if (!entry) return 'unknown';
    if (entry.applicable === true) return 'applied';
    const verdict = lastVerdict || {};
    const holds = function (list) {
      return (list || []).some(function (rule) { return rule.activation === 'code' && codeKey(rule.code) === k; });
    };
    if (holds(verdict.rejected)) return 'rejected';
    if (holds(verdict.stackBlocked)) return 'single';
    return 'pending';
  }

  function verifyRuleCode(code) {
    if (!rulesEnabled() || !ruleByCode(code)) return Promise.resolve({ state: 'unknown', changed: false });
    clearCodeRejection(code);
    rulesByCode(code).forEach(function (rule) {
      try { sessionStorage.removeItem(giftDismissKey(rule.variantId)); } catch (e) {}
    });
    codeCheckInFlight = true;
    return whenIdle()
      .then(function () { return handleCart(null, { deferPublish: true }); })
      .then(function (out) {
        codeCheckInFlight = false;
        return { state: codeOutcome(code, out), changed: !!(out && out.result && out.result.changed) };
      })
      .catch(function () {
        codeCheckInFlight = false;
        return { state: 'unknown', changed: false };
      });
  }

  function bindPickers() {
    document.querySelectorAll('[data-mg-sample-picker]').forEach(function (p) {
      if (!p.__mgBound) { p.__mgBound = true; bindSamplePicker(p); }
    });
  }

  // FoxTheme.pubsub is the only reliable channel for in-drawer quantity changes.
  // Our deferred script may boot before theme.js, so poll until it's defined.
  function subscribeToSleekPubSub(attempt) {
    attempt = attempt || 0;
    if (window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.subscribe && FoxTheme.pubsub.PUB_SUB_EVENTS) {
      FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, function (data) {
        bindPickers(); // Sleek may have replaced the drawer HTML
        handleCart(data && data.cart);
      });
      window.mgCI.pubsubBound = true;
      return;
    }
    if (attempt < 50) setTimeout(function () { subscribeToSleekPubSub(attempt + 1); }, 100);
  }

  let booted = false;
  function boot() {
    bindPickers();
    if (booted) return;
    booted = true;
    window.mgCI.booted = true;
    // MG-PATCH 2026-10-02 (CR-02): compare-at prices of the lines the page was rendered with.
    rememberCompareAt(readRulesData());

    subscribeToSleekPubSub();

    document.addEventListener('cart:updated', function (e) {
      const d = e.detail || {};
      bindPickers();
      handleCart(d.cart || d);
    });

    // Initial pass (covers a cart already meeting a tier / holding samples).
    getCart().then(handleCart).catch(function () {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
